import { useCallback, useEffect, useRef } from 'react';

import {
  type UseAuthContextType,
  getAuth0ErrorCode,
  getCurrentLocationPath,
  useAlert,
  useSessionStorage,
} from '@spotify/confidence-core-react';

import {
  invitedUserAuthorizationParams,
  selfServeSignupAuthorizationParams,
} from '../../../utils/authLoginParams';
import type { HandleLoginOptions } from '../CreateAccountFormContext';
import {
  getSignupAuthErrorMessage,
  getSignupAuthOutcome,
  isAuthDenialError,
  isSilentSignupAuthError,
} from '../signupUtils';
import type { useSignupTracking } from './useSignupTracking';

type UseSignupAuthActionsOptions = {
  inviteId?: string;
  authError?: Error;
  isAuthenticated: boolean;
  isLoading: boolean;
  beginAuthRedirect: ReturnType<typeof useSignupTracking>['beginAuthRedirect'];
  completeAuthRedirect: ReturnType<
    typeof useSignupTracking
  >['completeAuthRedirect'];
  hasReturnedAuthAttempt: boolean;
  loginWithRedirect: UseAuthContextType['loginWithRedirect'];
  logout: UseAuthContextType['logout'];
};

export const useSignupAuthActions = ({
  inviteId,
  authError,
  isAuthenticated,
  isLoading,
  beginAuthRedirect,
  completeAuthRedirect,
  hasReturnedAuthAttempt,
  loginWithRedirect,
  logout,
}: UseSignupAuthActionsOptions) => {
  const alert = useAlert();
  const [suppressAuthAlert, setSuppressAuthAlert] = useSessionStorage(
    'signup-suppress-auth-alert',
    false,
    { migrateKey: false },
  );

  useEffect(() => {
    if (isLoading || !hasReturnedAuthAttempt) return;
    if (authError) {
      completeAuthRedirect(
        getSignupAuthOutcome(authError),
        getAuth0ErrorCode(authError) ?? 'unknown',
      );
      setSuppressAuthAlert(false);
      return;
    }
    completeAuthRedirect(isAuthenticated ? 'success' : 'abandoned');
    setSuppressAuthAlert(false);
  }, [
    authError,
    completeAuthRedirect,
    hasReturnedAuthAttempt,
    isAuthenticated,
    isLoading,
    setSuppressAuthAlert,
  ]);

  const handledAuthErrorRef = useRef<Error>();
  useEffect(() => {
    if (!authError) {
      handledAuthErrorRef.current = undefined;
      return;
    }
    if (handledAuthErrorRef.current === authError) return;
    handledAuthErrorRef.current = authError;
    setSuppressAuthAlert(false);

    if (suppressAuthAlert || isSilentSignupAuthError(authError)) {
      return;
    }

    alert.post({
      message: getSignupAuthErrorMessage(authError),
      severity: 'error',
    });
  }, [alert, authError, setSuppressAuthAlert, suppressAuthAlert]);

  const handleLogin = useCallback(
    async (opts: HandleLoginOptions = {}) => {
      const { trigger = 'interaction', silent = false } = opts;

      setSuppressAuthAlert(silent);

      beginAuthRedirect({ trigger });

      try {
        await loginWithRedirect({
          authorizationParams: inviteId
            ? invitedUserAuthorizationParams
            : {
                ...selfServeSignupAuthorizationParams,
                ...(isAuthDenialError(authError)
                  ? { prompt: 'login' as const }
                  : {}),
              },
          appState: { returnTo: getCurrentLocationPath() },
        });
      } catch (loginError) {
        setSuppressAuthAlert(false);
        completeAuthRedirect(
          getSignupAuthOutcome(loginError),
          getAuth0ErrorCode(loginError) ?? 'unknown',
        );
        if (!silent && !isSilentSignupAuthError(loginError)) {
          alert.post({
            message: getSignupAuthErrorMessage(loginError),
            severity: 'error',
          });
        }
      }
    },
    [
      alert,
      authError,
      beginAuthRedirect,
      completeAuthRedirect,
      inviteId,
      loginWithRedirect,
      setSuppressAuthAlert,
    ],
  );

  const logoutTo = useCallback(
    async (returnTo: URL): Promise<void> => {
      try {
        await logout({
          logoutParams: {
            returnTo: returnTo.toString(),
          },
        });
      } catch {
        alert.post({
          message: "We couldn't sign you out. Try again.",
          severity: 'error',
        });
      }
    },
    [alert, logout],
  );

  const handleLogout = useCallback(async (): Promise<void> => {
    const returnTo = new URL('/signup', window.location.origin);
    returnTo.searchParams.set(
      'signupReturnTo',
      `${window.location.pathname}${window.location.search}`,
    );
    await logoutTo(returnTo);
  }, [logoutTo]);

  const handleSelfServeSignup = useCallback(async (): Promise<void> => {
    const returnTo = new URL('/signup', window.location.origin);
    await logoutTo(returnTo);
  }, [logoutTo]);

  return { handleLogin, handleLogout, handleSelfServeSignup };
};
